import React, { useState, useEffect, useCallback } from 'react';
import { Database, ChevronDown, ChevronRight, ArrowUpRight } from 'lucide-react';
import {
  listarAuditoria, mudancasLegiveis, identificaLinha, destinoDoRegistro,
  TABELAS_AUDITADAS, ACOES,
} from '../../../data/auditoria';

const POR_PAGINA = 50;

/**
 * O nome de cada aba do workflow, para o link dizer onde vai.
 *
 * São as ABAS e não as etapas da corrente: DG30 e DG60 dividem a de Prenhez.
 * Mapa local porque a lista de abas vive dentro do render do modal de etapas, e
 * o que a trilha precisa daqui é só o rótulo.
 */
const ROTULO_DA_ETAPA = {
  protocolo: 'Protocolo', opu: 'OPU', fiv: 'FIV', te: 'TE', pregnancy: 'Prenhez',
};

const quando = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Trilha de auditoria.
 *
 * A aba existia no menu desde sempre e não renderizava nada. O que ela mostra
 * é quem mexeu em quê: alteração campo a campo, e o conteúdo do que foi
 * excluído — que é a pergunta que ninguém consegue responder depois.
 */
const LogsTab = ({
  currentLab,
  // As três portas de um serviço, as mesmas do quadro. A trilha não abre nada
  // por conta própria: ela reusa quem já sabe abrir.
  services = [],
  clients = [],
  abrirCadastro,
  abrirFinanceiro,
  openWorkflowModal,
  abrirCliente,
  // De qual serviço é cada fatura. A parcela aponta para a fatura, e é a fatura
  // que aponta para o serviço — vem de quem já carregou as faturas, em vez de
  // uma consulta por linha da trilha.
  servicoDaFatura = {},
  servicoDaParcela = {},
}) => {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [temMais, setTemMais] = useState(false);
  const [abertos, setAbertos] = useState({});
  const [filtros, setFiltros] = useState({ tabela: '', acao: '' });

  const carregar = useCallback(async (paginaAlvo) => {
    if (!currentLab?.id) return;

    setCarregando(true);
    setErro(null);
    try {
      const linhas = await listarAuditoria({
        laboratoryId: currentLab.id,
        tabela: filtros.tabela || undefined,
        acao: filtros.acao || undefined,
        pagina: paginaAlvo,
        porPagina: POR_PAGINA,
      }) || [];

      // Uma página cheia sugere que há mais; é o suficiente para decidir se
      // mostra o botão, sem gastar uma contagem sobre a tabela inteira.
      setTemMais(linhas.length === POR_PAGINA);
      setRegistros(anteriores => paginaAlvo === 0 ? linhas : [...anteriores, ...linhas]);
    } catch (e) {
      console.error('Erro ao carregar a trilha:', e);
      // Sem cópia local de propósito: mostrar trilha velha faria alguém
      // concluir que algo não aconteceu.
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [currentLab?.id, filtros.tabela, filtros.acao]);

  useEffect(() => {
    setPagina(0);
    carregar(0);
  }, [carregar]);

  /**
   * O link de uma linha da trilha, quando existe destino E o alvo ainda existe.
   *
   * Duas condições, e a segunda é a que evita a decepção: `destinoDoRegistro` já
   * recusa a exclusão, mas um serviço pode ter saído da lista em memória por
   * outro caminho — e um link que abre uma tela vazia é pior que link nenhum.
   */
  const linkDoRegistro = (registro) => {
    const destino = destinoDoRegistro(registro, { servicoDaFatura, servicoDaParcela });
    if (!destino) return null;

    if (destino.porta === 'cliente') {
      const cliente = clients.find(c => c.id === destino.clientId);
      if (!cliente || !abrirCliente) return null;
      return { rotulo: destino.rotulo, abrir: () => abrirCliente(cliente) };
    }

    const service = services.find(x => x.id === destino.serviceId);
    if (!service) return null;

    if (destino.porta === 'etapas') {
      if (!openWorkflowModal) return null;
      const etapa = ROTULO_DA_ETAPA[destino.etapa];
      return {
        // Diz QUAL etapa: "Abrir etapa" sozinho ainda manda procurar.
        rotulo: etapa ? `Abrir ${etapa}` : destino.rotulo,
        abrir: () => openWorkflowModal(service, destino.etapa),
      };
    }
    if (destino.porta === 'financeiro') {
      if (!abrirFinanceiro) return null;
      return { rotulo: destino.rotulo, abrir: () => abrirFinanceiro(service) };
    }
    if (!abrirCadastro) return null;
    return { rotulo: destino.rotulo, abrir: () => abrirCadastro(service) };
  };

  const verMais = () => {
    const proxima = pagina + 1;
    setPagina(proxima);
    carregar(proxima);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b flex items-center space-x-3">
          <Database className="text-slate-400" size={20} />
          <div>
            <h3 className="text-lg font-medium text-slate-900">Trilha de auditoria</h3>
            <p className="text-sm text-slate-600">
              Quem alterou o quê, e o conteúdo do que foi excluído.
            </p>
          </div>
        </div>

        <div className="px-6 py-3 border-b bg-slate-50 flex flex-wrap gap-3">
          <select
            value={filtros.tabela}
            onChange={(e) => setFiltros({ ...filtros, tabela: e.target.value })}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos os registros</option>
            {Object.entries(TABELAS_AUDITADAS).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>{rotulo}</option>
            ))}
          </select>

          <select
            value={filtros.acao}
            onChange={(e) => setFiltros({ ...filtros, acao: e.target.value })}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas as ações</option>
            {Object.entries(ACOES).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>{rotulo}</option>
            ))}
          </select>
        </div>

        {erro && (
          <div className="px-6 py-4 text-sm text-slate-700 bg-slate-50 border-b">
            Não foi possível carregar a trilha: {erro}
          </div>
        )}

        <div className="divide-y divide-slate-200">
          {registros.map(registro => {
            const mudancas = mudancasLegiveis(registro.changes);
            const aberto = abertos[registro.id];
            const link = linkDoRegistro(registro);

            return (
              <div key={registro.id} className="px-6 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900">
                      <span className="font-medium">
                        {registro.actor_name || (registro.actor_id ? 'Usuário removido' : 'Fora do aplicativo')}
                      </span>
                      {' '}{(ACOES[registro.action] || registro.action).toLowerCase()}{' '}
                      <span className="text-slate-600">
                        {TABELAS_AUDITADAS[registro.table_name] || registro.table_name}
                      </span>
                      {' '}
                      <span className="font-mono text-xs text-slate-500">
                        {identificaLinha(registro)}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {quando(registro.at)}
                      {link && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            onClick={link.abrir}
                            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {link.rotulo}<ArrowUpRight size={11} />
                          </button>
                        </>
                      )}
                    </p>
                  </div>

                  {(mudancas.length > 0 || registro.snapshot) && (
                    <button
                      onClick={() => setAbertos(a => ({ ...a, [registro.id]: !a[registro.id] }))}
                      className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 flex-shrink-0"
                    >
                      {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {registro.action === 'UPDATE'
                        ? `${mudancas.length} ${mudancas.length === 1 ? 'campo' : 'campos'}`
                        : 'detalhes'}
                    </button>
                  )}
                </div>

                {aberto && (
                  <div className="mt-2 bg-slate-50 rounded-lg p-3 overflow-x-auto">
                    {mudancas.length > 0 ? (
                      <table className="w-full text-xs">
                        <tbody>
                          {mudancas.map(m => (
                            <tr key={m.campo}>
                              <td className="pr-3 py-0.5 text-slate-500 whitespace-nowrap">{m.campo}</td>
                              <td className="pr-2 py-0.5 text-slate-500 line-through">{m.de}</td>
                              <td className="py-0.5 text-slate-900 font-medium">{m.para}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      /* Conteúdo do que foi criado ou excluído. No DELETE é a
                         única fonte: a linha original não existe mais. */
                      <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all">
                        {JSON.stringify(registro.snapshot, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {registros.length === 0 && !carregando && !erro && (
          <div className="text-center py-12 text-slate-500">
            <Database className="mx-auto mb-3 text-slate-400" size={40} />
            <p className="text-sm">Nenhum registro na trilha ainda.</p>
          </div>
        )}

        {carregando && (
          <div className="text-center py-6 text-sm text-slate-500">Carregando...</div>
        )}

        {temMais && !carregando && (
          <div className="px-6 py-4 border-t">
            <button
              onClick={verMais}
              className="w-full py-2 text-sm text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Ver mais
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsTab;
